import React from 'react'
import {Button} from '../../core/shared/Button'
import Icon from '../../core/shared/Icon'
import {Input} from '../../core/shared/Input'
import {Select} from '../../core/shared/Select'
import {COUNTRIES} from './constants'
import {TaxesPromptContainer} from '../ui/payment-information'
import {usePaymentAddress} from './contexts/payment-address-context'
import {usePaymentCheckboxes} from './contexts/payment-checkboxes-context'
import {useRecurlyPayment} from './contexts/recurly-payment-context'
import {useSelector} from 'react-redux'
import {selectedMobileViewEnabledStatus} from '../../app-store/kpGlobalStateSlice'
import {fetchSelectedCountryRegionList} from '../../utils/address-validation-util'
import {Tooltip} from '../../core/shared/Tooltip'
// import {useViewport} from '../../utils/useViewport'

const InvoicePaymentForm = ({
  calculatingTaxes,
  businessAddress,
  handleCompleteOrder,
  bizphone,
  is5Order,
  isAddressUpdated,
  setIsAddressUpdated,
  setAddressInformation,
  updateParentAddress,
  country,
  disclaimerText,
  isMobile: isMobileFromProps = false,
}) => {
  const isMobileFromStore = useSelector(selectedMobileViewEnabledStatus)
  const isMobile = isMobileFromProps || isMobileFromStore
  const {checkBoxesValues, setCheckBoxesValues, isAgreeCheckboxDisabled} =
    usePaymentCheckboxes()
  const {setType} = useRecurlyPayment()
  React.useEffect(() => {
    setType('invoice')
  }, [setType])
  const {
    addressInformation,
    updateAddressField,
    resetAddress,
    setFromBusinessAddress,
    isValidAddress,
  } = usePaymentAddress()
  const [listOfStates, setListOfStates] = React.useState([])
  const [isTaxesButtonEnabled, setIsTaxesButtonEnabled] = React.useState(true)
  const formRef = React.useRef()
  const {useBusinessAddressInfo} = checkBoxesValues
  const [selectedCountry, setSelectedCountry] = React.useState(
    COUNTRIES.find(({value}) => value === country),
  )
  const countrySelectDisabled = true

  React.useEffect(() => {
    setSelectedCountry(COUNTRIES.find(({value}) => value === country))
  }, [country])

  const handleOnchange = ({target: {value, name}}) => {
    setCheckBoxesValues({
      ...checkBoxesValues,
      agreedTerms: false,
    })
    updateAddressField(name, value)
    if (!isMobile) setIsAddressUpdated(false)
    setIsTaxesButtonEnabled(true)
  }

  React.useEffect(() => {
    const getStates = () => {
      let stateLabel = ''
      if (['US', 'AU', 'NZ'].includes(selectedCountry.value)) {
        stateLabel = 'State'
      } else if (selectedCountry.value === 'KY') {
        stateLabel = 'City'
      } else if (selectedCountry.value === 'CA') {
        stateLabel = 'Province'
      } else if (selectedCountry.value === 'BB') {
        stateLabel = 'Parish'
      } else if (selectedCountry.value === 'GB') {
        stateLabel = 'County'
      }
      let blankOption = [{name: 'Select ' + stateLabel, value: ''}]
      const data = fetchSelectedCountryRegionList(selectedCountry?.value)
      setListOfStates([...blankOption, ...data])
    }
    if (selectedCountry || selectedCountry?.value) {
      getStates(selectedCountry?.value || selectedCountry)
    }
  }, [selectedCountry])

  React.useEffect(() => {
    if (useBusinessAddressInfo) {
      if (checkBoxesValues.updateBillingAddress) {
        setCheckBoxesValues({...checkBoxesValues, updateBillingAddress: false})
      }
      setFromBusinessAddress(businessAddress)
    } else {
      if (isMobile) {
        updateAddressField('address', '')
        updateAddressField('city', '')
        updateAddressField('firstName', '')
        updateAddressField('lastName', '')
        updateAddressField('state', '')
      } else {
        resetAddress()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useBusinessAddressInfo, businessAddress, listOfStates, isMobile])
  if (isMobile && !isMobileFromProps) {
    return (
      <form ref={formRef}>
        <>
          <p
            style={{
              fontWeight: 'bold',
              fontSize: '14px',
              margin: '36px 0 0 0',
            }}
          >
            Billing Address
          </p>
          <p style={{fontSize: '14px', margin: '12px 0 0 0'}}>
            We will mail the invoice to this address, please confirm that it is
            correct or make updates if needed.
          </p>
        </>
        {is5Order && isAddressUpdated && (
          <TaxesPromptContainer>
            <Icon name="info" color="#057AFF" />
            <p className="m-0">
              The current estimated taxes are based on your business address.
            </p>
          </TaxesPromptContainer>
        )}
        <div
          className="d-flex align-items-center mt-3 mb-3"
          style={{columnGap: '8px'}}
        >
          <input
            type="checkbox"
            name="useBusinessAddressInfo"
            checked={checkBoxesValues.useBusinessAddressInfo}
            onChange={e => {
              setCheckBoxesValues({
                ...checkBoxesValues,
                useBusinessAddressInfo: e.currentTarget.checked,
              })
            }}
            disabled={checkBoxesValues.agreedTerms}
          />
          <p style={{margin: '0 0 0.7rem 0'}}>Same as my business address</p>
        </div>
        <div
          className="d-flex"
          style={{flexDirection: 'column', rowGap: '15px'}}
        >
          <input
            className="recurly-element recurly-input w-100 mb-2"
            onChange={handleOnchange}
            data-recurly="first_name"
            placeholder="First Name"
            name="firstName"
            value={addressInformation.firstName}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
          <input
            className="recurly-element recurly-input w-100 mb-2"
            data-recurly="last_name"
            placeholder="Last Name"
            name="lastName"
            value={addressInformation.lastName}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
          <input
            className="recurly-element recurly-input w-100 mb-2"
            type="text"
            data-recurly="company"
            placeholder="Company"
            name="company"
            value={addressInformation.company}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
          <input
            className="recurly-element recurly-input w-100 mb-2"
            type="text"
            data-recurly="address1"
            placeholder="Street Address"
            name="address"
            value={addressInformation.address}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
          <input
            className="recurly-element recurly-input w-100 mb-2"
            type="text"
            data-recurly="city"
            placeholder="City"
            name="city"
            value={addressInformation.city}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
          <select
            className="recurly-element recurly-address w-100"
            style={{height: '35px'}}
            value={addressInformation?.state}
            onChange={e => {
              handleOnchange({
                target: {
                  name: 'state',
                  value: e.target.selectedOptions[0].value,
                },
              })
              setCheckBoxesValues({
                ...checkBoxesValues,
                agreedTerms: false,
              })
            }}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          >
            {listOfStates.map(({name, value}) => {
              return (
                <option key={value} value={value}>
                  {name}
                </option>
              )
            })}
          </select>
          <input
            hidden
            readOnly
            value={addressInformation?.state}
            type="text"
            className="recurly-element"
            data-recurly="state"
            placeholder="State"
          />
          <input
            className="recurly-element recurly-input w-100 mb-2"
            type="text"
            data-recurly="postal_code"
            placeholder="Postal Code"
            name="postalCode"
            value={addressInformation.postalCode}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
          <Select
            options={COUNTRIES.map(({name, value}) => ({label: name, value}))}
            label="Country"
            className="recurly-element recurly-address mt-3"
            data-recurly="country"
            value={selectedCountry?.value}
            onChange={e => {
              setSelectedCountry(
                COUNTRIES.find(c => c.value === e.target.value),
              )
              setCheckBoxesValues({
                ...checkBoxesValues,
                agreedTerms: false,
              })
            }}
            disabled={countrySelectDisabled}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
          />
          <input
            hidden
            readOnly
            type="text"
            placeholder="Country"
            value={selectedCountry?.value || selectedCountry}
            style={{fontSize: '18px'}}
            className="recurly-element recurly-address ml-2"
            data-recurly="country"
          />
        </div>
        <div className="mt-4">
          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            {isAgreeCheckboxDisabled ? (
              <Tooltip
                description="Please complete your payment information."
                variant="top"
              >
                <input
                  type="checkbox"
                  id="agreedTerms"
                  name="agreedTerms"
                  checked={checkBoxesValues.agreedTerms}
                  disabled
                  onChange={e => {
                    setCheckBoxesValues({
                      ...checkBoxesValues,
                      agreedTerms: e.currentTarget.checked,
                    })
                  }}
                />
              </Tooltip>
            ) : (
              <input
                type="checkbox"
                id="agreedTerms"
                name="agreedTerms"
                checked={checkBoxesValues.agreedTerms}
                onChange={e => {
                  setCheckBoxesValues({
                    ...checkBoxesValues,
                    agreedTerms: e.currentTarget.checked,
                  })
                }}
              />
            )}
            <label htmlFor="agreedTerms" style={{marginTop: '5px'}}>
              By checking this box, you agree to our{' '}
              <a
                target="_blank"
                href="https://corporate.thryv.com/terms/"
                rel="noreferrer"
              >
                Terms & Conditions
              </a>
            </label>
          </div>
          <p className="mt-2">
            By processing your payment, you agree that your personal payment
            payment processor—Adyen, N.V. and will be be subject to Adyen&apos;s{' '}
            <a
              target="_blank"
              href="https://www.adyen.com/policies-and-disclaimer/privacy-policy"
              rel="noreferrer"
            >
              privacy statement
            </a>
            .
          </p>
        </div>
        <div className="d-flex justify-content-center mt-4 mb-4">
          <Button
            variant={
              isMobile && ['AU', 'NZ'].includes(country) ? 'pay-now' : 'primary'
            }
            isActive={checkBoxesValues?.agreedTerms}
            onClick={() => {
              updateParentAddress(
                addressInformation,
                bizphone,
                handleCompleteOrder,
              )
            }}
            disabled={
              calculatingTaxes ||
              !isValidAddress ||
              !isTaxesButtonEnabled ||
              !checkBoxesValues?.agreedTerms
            }
          >
            {isMobile && ['AU', 'NZ'].includes(country)
              ? 'Complete Purchase'
              : 'Continue'}
          </Button>
        </div>
      </form>
    )
  } else {
    return (
      <form
        onSubmit={updateParentAddress}
        ref={formRef}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
        }}
      >
        <div
          style={{
            background: 'white',
            borderRadius: '10px',
            padding: isMobile ? '10px' : '16px',
          }}
        >
          {is5Order && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div className="section-heading section-heading--no-divider">
                <div className="section-heading-title-container">
                  <div
                    className="d-flex align-items-center"
                    style={{columnGap: '16px'}}
                  >
                    <p className="section-heading-title">Billing Address</p>
                    <div
                      className="d-flex align-items-center"
                      style={{columnGap: '6px'}}
                    >
                      <input
                        type="checkbox"
                        id="useBusinessAddressInfoDesktop"
                        name="useBusinessAddressInfo"
                        disabled={checkBoxesValues.agreedTerms}
                        checked={checkBoxesValues.useBusinessAddressInfo}
                        onChange={e => {
                          setCheckBoxesValues({
                            ...checkBoxesValues,
                            useBusinessAddressInfo: e.currentTarget.checked,
                            agreedTerms: false,
                          })
                        }}
                        title={
                          checkBoxesValues.agreedTerms
                            ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                            : undefined
                        }
                        style={{
                          cursor: 'pointer',
                          width: '16px',
                          height: '16px',
                          accentColor: '#5378FC',
                        }}
                      />
                      <label
                        htmlFor="useBusinessAddressInfoDesktop"
                        className="business-address-label"
                        style={{cursor: 'pointer', marginBottom: '0px'}}
                      >
                        Same as my business address
                      </label>
                    </div>
                  </div>
                  <p className="section-heading-subtitle">
                    We will mail the invoice to this address, please confirm
                    that it is correct or make updates if needed.
                  </p>
                </div>
              </div>
            </div>
          )}
          {!is5Order && (
            <div
              className="d-flex align-items-center mt-3 mb-3"
              style={{columnGap: '6px'}}
            >
              <input
                type="checkbox"
                id="useBusinessAddressInfo"
                name="useBusinessAddressInfo"
                disabled={checkBoxesValues.agreedTerms}
                checked={checkBoxesValues.useBusinessAddressInfo}
                onChange={e => {
                  setCheckBoxesValues({
                    ...checkBoxesValues,
                    useBusinessAddressInfo: e.currentTarget.checked,
                    agreedTerms: false,
                  })
                }}
                title={
                  checkBoxesValues.agreedTerms
                    ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                    : undefined
                }
                style={{
                  cursor: 'pointer',
                  width: '16px',
                  height: '16px',
                  accentColor: '#5378FC',
                }}
              />
              <label
                htmlFor="useBusinessAddressInfo"
                className="business-address-label"
                style={{cursor: 'pointer', marginBottom: '0px'}}
              >
                Same as my business address
              </label>
            </div>
          )}
          {is5Order && isAddressUpdated && (
            <TaxesPromptContainer>
              <Icon name="info" color="#057AFF" />
              <p className="m-0">
                The current estimated taxes are based on your business address.
              </p>
            </TaxesPromptContainer>
          )}
          <div
            className="d-flex justify-content-between"
            style={{
              gap: isMobile ? '10px' : '36px',
              marginBottom: isMobile ? '10px' : '20px',
            }}
          >
            <Input
              variant="payment"
              label="First Name"
              data-recurly="first_name"
              placeholder="First Name"
              name="firstName"
              onChange={handleOnchange}
              value={addressInformation.firstName}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                  : undefined
              }
            />
            <Input
              variant="payment"
              label="Last Name"
              data-recurly="last_name"
              placeholder="Last Name"
              name="lastName"
              onChange={handleOnchange}
              value={addressInformation.lastName}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                  : undefined
              }
            />
          </div>
          <Input
            variant="payment"
            label="Company"
            type="text"
            data-recurly="company"
            placeholder="Company"
            name="company"
            value={addressInformation.company}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
            style={{marginBottom: isMobile ? '10px' : '20px'}}
          />
          <Input
            variant="payment"
            label="Street Address"
            type="text"
            data-recurly="address1"
            placeholder="Street Address"
            name="address"
            value={addressInformation.address}
            onChange={handleOnchange}
            disabled={checkBoxesValues.agreedTerms}
            title={
              checkBoxesValues.agreedTerms
                ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                : undefined
            }
            style={{marginBottom: isMobile ? '10px' : '20px'}}
          />
          <div
            className={`responsive-address-container ${
              !is5Order && 'pr-1 address_container'
            }`}
            style={{marginBottom: isMobile ? '10px' : '20px'}}
          >
            <Input
              variant="payment"
              label="City"
              type="text"
              data-recurly="city"
              placeholder="City"
              name="city"
              value={addressInformation.city}
              onChange={handleOnchange}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                  : undefined
              }
            />
            <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
              <label className="payment-variant-label">State</label>
              <select
                className="recurly-address"
                style={{
                  height: '32px',
                  border: '1px solid #BFBFBF',
                  borderRadius: '5px',
                  fontFamily: 'Open Sans',
                  fontWeight: 400,
                  fontSize: '12px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  paddingLeft: '8px',
                }}
                value={addressInformation?.state}
                onChange={e => {
                  handleOnchange({
                    target: {
                      name: 'state',
                      value: e.target.selectedOptions[0].value,
                    },
                  })
                  setCheckBoxesValues({
                    ...checkBoxesValues,
                    agreedTerms: false,
                  })
                }}
                disabled={checkBoxesValues.agreedTerms}
                title={
                  checkBoxesValues.agreedTerms
                    ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                    : undefined
                }
              >
                {listOfStates.map(({name, value}) => {
                  return (
                    <option key={value} value={value}>
                      {name}
                    </option>
                  )
                })}
              </select>
            </div>
            <input
              hidden
              readOnly
              value={addressInformation?.state}
              type="text"
              className="recurly-element"
              data-recurly="state"
              placeholder="State"
            />
            <Input
              variant="payment"
              label="Postal Code"
              type="text"
              data-recurly="postal_code"
              placeholder="Postal Code"
              name="postalCode"
              value={addressInformation.postalCode}
              onChange={handleOnchange}
              disabled={checkBoxesValues.agreedTerms}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                  : undefined
              }
            />
          </div>
          {is5Order && (
            <div
              style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : undefined,
                gap: isMobile ? '10px' : '16px',
                alignItems: isMobile ? 'stretch' : 'flex-start',
              }}
            >
              <div style={{flex: 1}}>
                <Select
                  options={COUNTRIES.map(({name, value}) => ({
                    label: name,
                    value,
                  }))}
                  label="Country"
                  size="sm"
                  variant="payment"
                  className="payment-country-select"
                  data-recurly="country"
                  value={selectedCountry?.value}
                  onChange={e => {
                    setSelectedCountry(
                      COUNTRIES.find(c => c.value === e.target.value),
                    )
                    setCheckBoxesValues({
                      ...checkBoxesValues,
                      agreedTerms: false,
                    })
                  }}
                  disabled={countrySelectDisabled}
                  title={
                    checkBoxesValues.agreedTerms
                      ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                      : undefined
                  }
                />
              </div>
              <div
                style={{
                  flex: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  paddingTop: isMobile ? '10px' : '16px',
                }}
              >
                {disclaimerText && (
                  <p className="card-number-helper-text" style={{margin: 0}}>
                    {disclaimerText}
                  </p>
                )}
                <p className="card-number-helper-text" style={{margin: 0}}>
                  {
                    "By processing your payment, you agree that your personal information will be shared with Thryv's payment processor—Adyen, N.V. and will be subject to Adyen's "
                  }
                  <a
                    target="_blank"
                    href="https://www.adyen.com/policies-and-disclaimer/privacy-policy"
                    rel="noreferrer"
                    style={{fontWeight: 700, color: '#5378FC'}}
                  >
                    privacy statement
                  </a>
                  {'.'}
                </p>
              </div>
            </div>
          )}
          {!is5Order && (
            <Select
              options={COUNTRIES.map(({name, value}) => ({label: name, value}))}
              label="Country"
              className="recurly-element recurly-address mt-3"
              data-recurly="country"
              value={selectedCountry?.value}
              onChange={e => {
                setSelectedCountry(
                  COUNTRIES.find(c => c.value === e.target.value),
                )
                setCheckBoxesValues({
                  ...checkBoxesValues,
                  agreedTerms: false,
                })
              }}
              disabled={countrySelectDisabled}
              title={
                checkBoxesValues.agreedTerms
                  ? 'To update your payment method or address, please uncheck the Terms & Conditions box.'
                  : undefined
              }
            />
          )}
          <input
            hidden
            readOnly
            type="text"
            placeholder="Country"
            value={selectedCountry?.value || selectedCountry}
            style={{
              fontSize: '18px',
            }}
            className="recurly-element recurly-address ml-2"
            data-recurly="country"
          />
        </div>
      </form>
    )
  }
}

export {InvoicePaymentForm}
